/**
 * Verlauf eines Fahrzeugs (Halterwechsel, Standortwechsel, KM-Stand, manuelle Notizen).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { History, Plus, Loader2, Gauge, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI } from '../../lib/api';

const EVENT_LABELS = {
  created: { label: 'Angelegt', color: 'bg-blue-100 text-blue-800' },
  imported: { label: 'Import', color: 'bg-indigo-100 text-indigo-800' },
  field_change: { label: 'Änderung', color: 'bg-amber-100 text-amber-800' },
  km_update: { label: 'KM-Stand', color: 'bg-emerald-100 text-emerald-800' },
  note: { label: 'Notiz', color: 'bg-slate-100 text-slate-800' },
};

export const VehicleHistoryDialog = ({ open, onOpenChange, vehicle, canEdit, onChanged }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [kmValue, setKmValue] = useState('');

  const load = useCallback(async () => {
    if (!vehicle?.id) return;
    setLoading(true);
    try {
      const r = await vehiclesAPI.history(vehicle.id);
      setEntries(r.data);
    } catch {
      toast.error('Verlauf konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [vehicle?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const onAddNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await vehiclesAPI.addNote(vehicle.id, noteText.trim());
      setNoteText('');
      toast.success('Notiz hinzugefügt');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const onUpdateKm = async () => {
    const km = parseInt(kmValue, 10);
    if (!Number.isFinite(km) || km < 0) {
      toast.error('Bitte gültigen Kilometerstand eingeben');
      return;
    }
    setSaving(true);
    try {
      await vehiclesAPI.updateKm(vehicle.id, km, null);
      setKmValue('');
      toast.success('Kilometerstand aktualisiert');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  if (!vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Verlauf: {vehicle.license_plate} · {vehicle.brand} {vehicle.model}
          </DialogTitle>
        </DialogHeader>

        {canEdit && (
          <div className="space-y-3 rounded-lg border p-3 bg-secondary/30">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Kilometerstand</label>
                <Input type="number" placeholder={vehicle.current_km ? `Aktuell: ${vehicle.current_km}` : 'KM eingeben'} value={kmValue} onChange={e => setKmValue(e.target.value)} />
              </div>
              <Button onClick={onUpdateKm} disabled={saving || !kmValue} data-testid="btn-update-km">
                <Gauge className="h-4 w-4 mr-1" /> Aktualisieren
              </Button>
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Notiz hinzufügen</label>
                <Textarea rows={2} value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="z.B. Reifenwechsel durchgeführt, neuer Aufkleber angebracht..." />
              </div>
              <Button onClick={onAddNote} disabled={saving || !noteText.trim()} data-testid="btn-add-note">
                <Plus className="h-4 w-4 mr-1" /> Hinzufügen
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {loading ? (
            [1,2,3].map(i => <Skeleton key={i} className="h-16" />)
          ) : entries.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Keine Einträge im Verlauf.</p>
          ) : (
            entries.map(e => {
              const meta = EVENT_LABELS[e.event_type] || EVENT_LABELS.note;
              return (
                <div key={e.id} className="border rounded-lg p-3 text-sm" data-testid={`history-entry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={meta.color}>{meta.label}</Badge>
                        {e.field_name && <span className="font-medium text-sm">{e.field_name}</span>}
                      </div>
                      {(e.old_value || e.new_value) && (
                        <p className="text-xs text-muted-foreground">
                          {e.old_value !== null && e.old_value !== undefined ? <code className="bg-secondary px-1 rounded">{e.old_value || '—'}</code> : null}
                          {e.old_value !== null && e.new_value ? <span> → </span> : null}
                          {e.new_value !== null && e.new_value !== undefined ? <code className="bg-secondary px-1 rounded">{e.new_value}</code> : null}
                        </p>
                      )}
                      {e.comment && (
                        <p className="text-sm flex items-start gap-1 mt-1">
                          <MessageSquare className="h-3 w-3 mt-1 text-muted-foreground flex-shrink-0" />
                          <span>{e.comment}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                      <p>{new Date(e.timestamp).toLocaleString('de-DE')}</p>
                      <p>{e.changed_by_name}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
